import type { FirebaseApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';

import { env } from '@/config/env';

// Firebase is loaded lazily (dynamic import) so ~250 kB of SDK stays out of the
// initial bundle — it's only fetched when a user actually enables push.
let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  const cfg = env.firebase;
  if (!cfg) return null;
  const { getMessaging, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return null;
  if (!messaging) {
    const { initializeApp } = await import('firebase/app');
    app ??= initializeApp({
      apiKey: cfg.apiKey,
      authDomain: cfg.authDomain,
      projectId: cfg.projectId,
      messagingSenderId: cfg.messagingSenderId,
      appId: cfg.appId,
    });
    messaging = getMessaging(app);
  }
  return messaging;
}

export async function isPushSupported(): Promise<boolean> {
  if (
    !env.pushEnabled ||
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('Notification' in window)
  ) {
    return false;
  }
  const { isSupported } = await import('firebase/messaging');
  return isSupported();
}

/**
 * Requests notification permission and returns an FCM token, or null if push is
 * unavailable / permission was denied.
 *
 * Reuses the app's single PWA service worker (src/sw.ts, registered from
 * main.tsx) rather than registering a separate one — the FCM background
 * handler lives inside that same file, since only one SW can control scope
 * `/`. `navigator.serviceWorker.ready` waits for it to be active.
 */
export async function requestPushToken(): Promise<string | null> {
  const cfg = env.firebase;
  const m = await getMessagingInstance();
  if (!cfg || !m) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const registration = await navigator.serviceWorker.ready;
  const { getToken } = await import('firebase/messaging');
  const token = await getToken(m, {
    vapidKey: cfg.vapidKey,
    serviceWorkerRegistration: registration,
  });
  return token || null;
}

/** Subscribes to foreground messages. Returns an unsubscribe function. */
export async function onForegroundMessage(
  callback: (message: { title: string; body: string }) => void,
): Promise<() => void> {
  const m = await getMessagingInstance();
  if (!m) return () => undefined;
  const { onMessage } = await import('firebase/messaging');
  return onMessage(m, (payload) => {
    callback({
      title: payload.notification?.title ?? '',
      body: payload.notification?.body ?? '',
    });
  });
}
